import Promise from 'es6-promise-promise';
import scrollMonitor from 'scrollmonitor';
import {publishEvent, getPageActionData} from '../helpers/utilities';
import {appendParamsToURL} from '../helpers/urlUtilities';
import {callbackOnElements, objectAssign} from '@borngroup/born-utilities';

const
    DATA_LAYER = 'dataLayer',
    DEBUG_MODE = false,
    ENTER_VIEWPORT_GROUP = {};

/**
 * Sets up event tracking on specific element. Uses the 'data-tracking' attribute to determine what to track.
 */

export default class TrackingLayer {
    constructor(options = {}) {
        callbackOnElements.call(this, '[data-tracking-position]', this.setPosition);
        callbackOnElements.call(this, options.elements, this.setElement);
    }

    setPosition(el, index) {
        el.setAttribute('data-tracking-position', index + 1);
    }

    /**
     * Sets up individual elements that have the data-tracking property
     * @param {[type]} el [description]
     */
    setElement(el) {
        //If a 'data-tracking' is present, then those properties will  be used instead.
        try {
            let options = el.getAttribute('data-tracking') ? JSON.parse(el.getAttribute('data-tracking')) : {};
            el.trackingLayer = {};

            if (Array.isArray(options)) {
                options.forEach(this.setHandler.bind(this, el));
            } else {
                this.setHandler(el, options);
            }
        } catch (e) {
            return false;
        }
    }

    /**
     * Adds a tracking handler to the passed element
     * @param {[Object]} options:
     *        el: [Node]
     *        events: [String] Event to listen to.
     *        trackEvent: [String] Name of the data layer object on which this event will be tracked.
     *        trackObject: [Object] Key value pairs to be reported back to the tracking layer.
     *        trackHandler: [String] Optional. Handler for the event.
     */
    setHandler(el, options) {
        let handler = this.eventTracking,
            eventList = options.events;

        if (!eventList) {
            if (el.nodeName === 'INPUT' || el.nodeName === 'SELECT' || el.nodeName === 'TEXTAREA') {
                eventList = 'change';
            } else {
                eventList = 'click';
            }
        }

        eventList = eventList.trim().split(/(?:,| )+/);

        //Attach a listener for each passed event.
        eventList.forEach(function(currentEvent) {
            if (currentEvent === 'pageload') {
                //If `DATA_LAYER` is available, run immediately, otherwise wait until resources are loaded.
                if (window[DATA_LAYER]) {
                    handler.call(this, el, options);
                } else {
                    window.addEventListener('load', handler.bind(this, el, options));
                }

            } else if (currentEvent === 'iframeclick') {
                //Very hacky way to track a click on an iframe.
                //Checks if when on window blur the target is an iframe that matches either the tracked element or the delegated element.
                window.addEventListener('blur', function() {
                    //Forces browsers to run this immediately instead of deferring it.
                    window.setTimeout(function() {
                        if (document.activeElement instanceof HTMLIFrameElement && (document.activeElement === el || (options.delegate && document.activeElement.matches(options.delegate))) ) {
                            handler.call(this, el, options);
                        }
                    }.bind(this), 0);
                }.bind(this));
            } else if (currentEvent === 'enterViewport') {
                scrollMonitor.create(el, -200).one(currentEvent, function() {
                    if (options.enterViewportGroup) {
                        this.getInViewportItems(el, options, options.enterViewportGroup);
                    } else {
                        handler.call(this, el, options);
                    }
                }.bind(this));
            } else {
                //Run delegated event from the body to allow for DOM insertions to also work.
                if (options.delegate) {
                    document.body.addEventListener(currentEvent, function(evt) {
                        if (evt.target.matches(options.delegate)) {
                            handler.call(this, evt.target, options);
                        }
                    }.bind(this));
                } else {
                    el.addEventListener(currentEvent, handler.bind(this, el, options));
                }
            }
        }.bind(this));

        el.removeAttribute('data-tracking');
    }

    /**
     * Generates an array with related "in viewport" items that need to be pushed together whenever they enter the viewport.
     * A slight timeout is added to allow JS to collect however many items are now in view when the user stops scrolling.
     */
    getInViewportItems(el, options, groupID) {
        window.clearTimeout(this.enterViewportTimeout);

        ENTER_VIEWPORT_GROUP[groupID] = ENTER_VIEWPORT_GROUP[groupID] || [];

        ENTER_VIEWPORT_GROUP[groupID].push(el);

        this.enterViewportTimeout = window.setTimeout(function() {
            options.childrenNodeList = ENTER_VIEWPORT_GROUP[groupID];

            this.eventTracking(el, options);

            //Clear the current group after tracking data has been processed.
            ENTER_VIEWPORT_GROUP[groupID] = [];
        }.bind(this), 100);
    }

    /**
     * Determines what to push as the value for a given property name.
     * First checks for properties, attributes, and Resources then, if all fails, returns the string
     */
    getPropertyValue(el, propertyReference, options) {
        //Grab a specific context based on the closest element.
        if (propertyReference === '--context') {
            let matchingContext = el.closest('[data-tracking-context]');

            if (matchingContext) {
                return matchingContext.getAttribute('data-tracking-context');
            }
        } else if (propertyReference.indexOf('--context-') !== -1) {
            let contextType = propertyReference.replace('--context-', ''),
                matchingContext = el.closest(`[data-${contextType}]`);

            if (matchingContext) {
                return matchingContext.getAttribute(`data-${contextType}`);
            }
        } else if (propertyReference === '--childrenData') {
            let childrenDataArray = [],
                childrenNodeList = options.childrenNodeList || el.querySelectorAll('[data-tracking-id]');

            [].forEach.call(childrenNodeList, function(currentEl) {
                //Attempt to find a child element with the right attributes, otherwise default to the current element.
                currentEl = currentEl.querySelector('[data-content-component], [data-product-tile]') || currentEl;

                if (currentEl.hasAttribute('data-content-component')) {
                    childrenDataArray.push({
                        promoName: currentEl.getAttribute('data-tracking-id'),
                        promoPosition: currentEl.getAttribute('data-tracking-position'),
                    });
                } else if (currentEl.hasAttribute('data-product-tile')) {
                    childrenDataArray.push({
                        pid: currentEl.getAttribute('data-tracking-id'),
                        pos: currentEl.getAttribute('data-tracking-position'),
                    });
                }
            });

            if (childrenDataArray.length) {
                return childrenDataArray;
            }
        } else if (propertyReference === '--content') {
            return el.textContent.trim();
        } else if (propertyReference === '--dropdownDisplayValue') {
            return el.selectedOptions[0].textContent.trim();
        }

        return el[propertyReference] || el.getAttribute(propertyReference) || window.SFRA.Resources[propertyReference] || propertyReference;
    }

    /**
     * Loops through the properties defined in trackObject and gets their real values by calling getPropertyValue;
     * @param  {[HTMLElement]} el         [description]
     * @param  {[Object]} trackObject [description]
     * @return {[Object]}            [description]
     */
    getTrackObject(el, sourceObject, options) {
        let trackObject = {};

        if (typeof sourceObject === 'object') {
            for (let key in sourceObject) {
                trackObject[key] = this.getPropertyValue(el, sourceObject[key], options);
            }
        }

        return trackObject;
    }

    /**
     * Default tracking method. Just pushes back a report value to the Data Layer
     * @param  {[String | Boolean]} options.trackObject [property, attribute, or string to report]
     * @param  {[String]} options.trackProperty  [Data Layer variable to report to]
     * @param  {[String]} options.trackEvent [Name of the layer object to attach the variable to. ex: "interactions", "event", etc.]
     */
    eventTracking(el, options, evt) {
        //The "data-disable-redirect" attribute can be set on an anchor element.
        //This attribute goes along functionalities like modal triggers, which should not redirect the user.
        //Use `evt.target.getAttribute('href')` instead of `evt.target.href` to avoid incorrectly processing an SVG target.
        let hrefURL = evt && evt.target && evt.target.getAttribute('href') && !evt.target.hasAttribute('data-disable-redirect') ? evt.target.getAttribute('href') : null,
            hrefTarget = evt && evt.target && evt.target.getAttribute('target'),
            trackObject = this.getTrackObject(el, options.trackObject, options),
            retrieveAsyncData = options.asyncParams || options.asyncURL,
            manualRedirect = hrefURL && !evt.metaKey && retrieveAsyncData;

        //Stops anchor clicks within a given data-tracking container if it should retrireve server data.
        if (manualRedirect) {
            evt.preventDefault();
        }

        //Set the appropriate properties in the `trackObject`, which is later used to request server data.
        if (retrieveAsyncData) {
            trackObject.asyncURL = options.asyncURL || window.SFRA.Urls.GTMEventView;
            trackObject.asyncParams = this.getTrackObject(el, options.asyncParams, options);
        }

        if (trackObject && trackObject.asyncParams && options.trackEvent === 'eePromoView') {
            // check if the promo name is null
            let promotions = trackObject.asyncParams.promotions;
            if (promotions && Array.isArray(promotions) && promotions.length > 0) {
                promotions = promotions.filter(promo => {
                    return promo.promoName !== 'null';
                })
            }

            if (!promotions || promotions.length === 0 || !Array.isArray(promotions)) {
                return null;
            }
            trackObject.asyncParams.promotions = promotions;
        }

        if (trackObject && trackObject.asyncParams && options.trackEvent === 'eePromoClick') {
            // check if the promo name is null
            let promoName = trackObject.asyncParams.promoName && trackObject.asyncParams.promoName !== 'null' && trackObject.asyncParams.promoName !== 'undefined' ? trackObject.asyncParams.promoName : null;
            if (!promoName) {
                return null;
            }
        }

        if (options.update) {
            TrackingLayer.updateData(options.update, trackObject);
        } else {
            TrackingLayer.pushData(options.trackEvent, trackObject, options.trackMethod).then(function() {
                //After server data has been retrieved, redirect the user to the target anchor's URL.
                if (manualRedirect) {
                    if (hrefTarget === '_blank') {
                        window.open(hrefURL, '_blank', 'noopener,noreferrer');
                    } else {
                        window.location = hrefURL;
                    }
                }
            });
        }
    }

    /**
     * Loops through a list of URLs to load additional data for the data-layer.
     * @param  {[type]} trackObject [description]
     * @param  {[type]} index       [description]
     * @return {[type]}             [description]
     */
    static getAsyncObject(trackEvent, trackObject, index) {
        let currentIndex = index || 0,
            isArray = Array.isArray(trackObject.asyncURL),
            asyncURL = isArray ? trackObject.asyncURL[currentIndex] : trackObject.asyncURL;

        asyncURL = TrackingLayer.setAsyncURLParams(trackEvent, trackObject, asyncURL);

        return Promise.resolve($.get(asyncURL)).then(function(response) {
            objectAssign(response, trackObject);

            currentIndex++;

            if (isArray && trackObject.asyncURL[currentIndex]) {
                return TrackingLayer.getAsyncObject(trackEvent, response, currentIndex);
            } else {
                delete response.asyncURL;
                delete response.asyncParams;
            }

            return response;
        });
    }

    /**
     * Triggers tracking for `elements` with the "action.done" event.
     * @param  {[HTMLElement]} el
     */
    static triggerEvent(elements) {
        callbackOnElements(elements, function(currentEl) {
            publishEvent('actiondone', false, currentEl);
        });
    }

    /**
     * Attaches necessary URL parameters for the backend to return the desired tracking data from the current event.
     */
    static setAsyncURLParams(trackEvent, trackObject, url) {
        let pageData = getPageActionData() || {};

        return appendParamsToURL(
            url,
            {
                eventType: trackObject.event || trackEvent,
                reqPath: (trackObject.reqPath || pageData.action),
                qString: (trackObject.qString || pageData.queryString || window.location.search.substring(1)),
                eventParams: trackObject.asyncParams ? JSON.stringify(trackObject.asyncParams) : ''
            }
        );
    }

    static updateData(targetObject, trackObject, trackMethod) {
        if (window[DATA_LAYER] && window[targetObject]) {
            objectAssign(window[targetObject], trackObject);

            trackMethod = trackMethod && window[DATA_LAYER][trackMethod] ? trackMethod : 'view';

            if (window[DATA_LAYER][trackMethod]) {
                window[DATA_LAYER][trackMethod](window[targetObject]);
            }

            if (DEBUG_MODE) {
                console.log(trackObject);
            }
        }
    }

    static pushData(trackEvent, trackObject, trackMethodName = 'push') {
        if (!window[DATA_LAYER] || !trackEvent) {
            return $.Deferred().resolve({}).promise();
        }

        if (typeof trackObject === 'object' && (trackObject.asyncURL || trackObject.asyncParams)) {
            trackObject.asyncURL = trackObject.asyncURL || window.SFRA.Urls.GTMEventView;

            return TrackingLayer.getAsyncObject(trackEvent, trackObject).then(function(response) {
                return TrackingLayer.runTrackingMethod(trackEvent, response, trackMethodName);
            });
        }

        return TrackingLayer.runTrackingMethod(trackEvent, trackObject, trackMethodName);
    }

    /**
     * Provide a safe fallback in case user has AdBlockers enabled.
     * This is necessary since some of the events require collecting data before redirecting the user, and the redirection happens in the JS.
     * @return {[type]} [description]
     */
    static safeTrackMethod(trackMethod) {
        let options = [].slice.call(arguments, 1);

        try {
            trackMethod.apply(this, options);
        } catch (e) {
            console.error(e);
        }
    }

    static runTrackingMethod(trackEvent, trackObject, trackMethodName = 'push') {
        let trackMethod = window[DATA_LAYER][trackMethodName] || window[DATA_LAYER];

        if (typeof trackObject === 'object') {
            trackObject.event = trackObject.hasOwnProperty('trackEvent') ? trackObject['trackEvent'] : trackEvent;

            if (trackObject.dataLayers && trackObject.dataLayers.length) {
                trackObject.dataLayers.forEach(function(currentLayer) {
                    TrackingLayer.safeTrackMethod(trackMethod, currentLayer);
                });
            } else {
                TrackingLayer.safeTrackMethod(trackMethod, trackObject);
            }
        } else {
            TrackingLayer.safeTrackMethod(trackMethod, trackEvent, trackObject);
        }

        if (DEBUG_MODE) {
            console.log(trackObject);
        }

        return $.Deferred().resolve(trackObject).promise();
    }
}
